import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListPlus, Users, Calendar, Building2, Leaf, FileCheck, UserPlus, ClipboardList, Map, MessageSquare, Video, CheckCircle, ArrowRight, Sprout, Plane as Plant, Tractor, Bell, Calendar as CalendarIcon, Plus } from 'lucide-react';
import { Meta } from '../components/Meta';

export function Tutorials() {
  const { t } = useTranslation();
  const tutorials = [
    { titleKey: 'creatingListing', icon: ListPlus, sections: [
        { titleKey: 'practitionerListing', stepsKeys: ['practitionerStep1', 'practitionerStep2', 'practitionerStep3', 'practitionerStep4', 'practitionerStep5'] },
        { titleKey: 'eventListing', stepsKeys: ['eventStep1', 'eventStep2', 'eventStep3', 'eventStep4', 'eventStep5'] },
        { titleKey: 'venueListing', stepsKeys: ['venueStep1', 'venueStep2', 'venueStep3', 'venueStep4', 'venueStep5'] }
      ]
    },
    { titleKey: 'usingAgriTool', icon: Tractor, sections: [
        { titleKey: 'settingUpField', stepsKeys: ['fieldStep1', 'fieldStep2', 'fieldStep3', 'fieldStep4', 'fieldStep5'] },
        { titleKey: 'managingTasks', stepsKeys: ['tasksStep1', 'tasksStep2', 'tasksStep3', 'tasksStep4', 'tasksStep5'] },
        { titleKey: 'teamCollaboration', stepsKeys: ['teamStep1', 'teamStep2', 'teamStep3', 'teamStep4', 'teamStep5'] },
        { titleKey: 'usingPlantInfo', stepsKeys: ['plantStep1', 'plantStep2', 'plantStep3', 'plantStep4', 'plantStep5'] }
      ]
    },
    { titleKey: 'communityFeatures', icon: Users, sections: [
        { titleKey: 'creatingCommunity', stepsKeys: ['createCommunityStep1', 'createCommunityStep2', 'createCommunityStep3', 'createCommunityStep4', 'createCommunityStep5'] },
        { titleKey: 'managingMembers', stepsKeys: ['manageMembersStep1', 'manageMembersStep2', 'manageMembersStep3', 'manageMembersStep4', 'manageMembersStep5'] }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3F7EE] to-white">
      <Meta 
        title={t('tutorials.metaTitle')}
        description={t('tutorials.metaDesc')}
      />

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-page mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-gelica text-content leading-tight mb-6">
            {t('tutorials.title')}
          </h1>
          <p className="text-xl text-content/70 max-w-3xl mx-auto">
            {t('tutorials.subtitle')}
          </p>
        </div>
      </div>

      {/* Tutorials Grid */}
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="space-y-16">
          {tutorials.map((tutorial, index) => {
            const Icon = tutorial.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
              >
                <div className="flex items-center mb-8">
                  <div className="p-3 bg-accent-base/10 rounded-lg">
                    <Icon className="h-8 w-8 text-accent-text" />
                  </div>
                  <h2 className="text-2xl font-semibold text-content ml-4">
                    {t(`tutorials.${tutorial.titleKey}`)}
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {tutorial.sections.map((section, sIndex) => (
                    <div 
                      key={sIndex}
                      className="bg-accent-base/5 rounded-xl p-6"
                    >
                      <h3 className="font-medium text-content mb-4">
                        {t(`tutorials.${section.titleKey}`)}
                      </h3>
                      <ol className="space-y-3">
                        {section.stepsKeys.map((key, stepIndex) => (
                          <li 
                            key={stepIndex}
                            className="flex items-start text-content/70"
                          >
                            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-text/10 text-accent-text text-sm mr-3">
                              {stepIndex + 1}
                            </span>
                            {t(`tutorials.${key}`)}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white py-24">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-gelica font-bold text-content mb-4">
              {t('tutorials.needMoreHelp')}
            </h2>
            <p className="text-lg text-content/70">
              {t('tutorials.needMoreHelpDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-accent-base/5 rounded-xl p-8">
              <MessageSquare className="h-8 w-8 text-accent-text mb-4" />
              <h3 className="text-xl font-semibold text-content mb-2">
                {t('tutorials.communitySupport')}
              </h3>
              <p className="text-content/70">
                {t('tutorials.communitySupportDesc')}
              </p>
            </div>

            <div className="bg-accent-base/5 rounded-xl p-8">
              <Video className="h-8 w-8 text-accent-text mb-4" />
              <h3 className="text-xl font-semibold text-content mb-2">
                {t('tutorials.videoGuides')}
              </h3>
              <p className="text-content/70">
                {t('tutorials.videoGuidesDesc')}
              </p>
            </div>

            <div className="bg-accent-base/5 rounded-xl p-8">
              <Bell className="h-8 w-8 text-accent-text mb-4" />
              <h3 className="text-xl font-semibold text-content mb-2">
                {t('tutorials.supportTeam')}
              </h3>
              <p className="text-content/70">
                {t('tutorials.supportTeamDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-b from-white to-[#F3F7EE]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-gelica font-bold text-content mb-6">
            {t('tutorials.readyToStart')}
          </h2>
          <p className="text-xl text-content/70 mb-8">
            {t('tutorials.readyToStartDesc')}
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-accent-text hover:bg-accent-text/90 transition-colors"
          >
            {t('tutorials.getStarted')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}